import React, { useState, useEffect, useReducer, useRef } from "react";

import { t } from "i18next";

import BaseAlertModal from "./BaseAlertModal";
import BaseActionButton from "../Buttons/BaseActionButton";
import MainLoadingButton from "../Buttons/MainLoadingButton";
import PromotionCodesAPI from "../../API/promotionCodes.api";
import { SuccessAlert } from "../../utils/Alerts";
import { REDUCER_ACTIONS, REDUCER_STATES } from "../../utils/ReducerConstants";

const undoDelay = 3000;

const reducerActions = REDUCER_ACTIONS.sharePromotionCode;
const reducerStates = REDUCER_STATES.sharePromotionCode;

const emailReducer = (state, action) => {
    switch (action) {
        case reducerActions.initial:
            return { emailState: reducerStates.initial };
        case reducerActions.sendClicked:
            return { emailState: reducerStates.sendClicked };
        case reducerActions.emailSent:
            return { emailState: reducerStates.emailSent };
        case reducerActions.emailFailed:
            return { emailState: reducerStates.emailFailed };
        default:
            return state;
    }
};

const ShareModal = ({
    isVisible,
    setIsVisible,
    selectedRow,
    setSelectedRow,
}) => {
    const [userEmail, setUserEmail] = useState("");
    const [counter, setCounter] = useState(undoDelay / 1000);
    const [reducedState, dispatch] = useReducer(emailReducer, {
        emailState: reducerStates.initial,
    });

    const intervalId = useRef();
    const delayId = useRef();

    useEffect(() => {
        if (!isVisible) reset();
    }, [isVisible]);

    useEffect(() => {
        handleUndoCountDown();
    }, [reducedState.emailState]);

    const reset = () => {
        setUserEmail("");
        setSelectedRow({});
        dispatch(reducerActions.initial);
    };

    const softReset = (emailValue) => {
        setUserEmail(emailValue);
        dispatch(reducerActions.initial);
    };

    const handleUndoCountDown = () => {
        if (compareEmailState(reducerStates.sendClicked)) {
            intervalId.current = setInterval(() => {
                setCounter((prev) => prev - 1);
            }, 1000);
        } else {
            setCounter(undoDelay / 1000);
            clearInterval(intervalId.current);
        }
    };

    const handleEmailChange = (e) => {
        const isSoftReset =
            compareEmailState(reducerStates.emailSent) ||
            compareEmailState(reducerStates.emailFailed);

        isSoftReset ? softReset(e.target.value) : setUserEmail(e.target.value);
    };

    const handleUndoClicked = () => {
        dispatch(reducerActions.initial);
        clearTimeout(delayId.current);
    };

    const handleSendButtonClicked = async () => {
        dispatch(reducerActions.sendClicked);
        delayId.current = setTimeout(async () => {
            await sendEmail();
        }, undoDelay);
    };

    const sendEmail = async () => {
        dispatch(reducerActions.sendingEmail);
        const result = await PromotionCodesAPI.sharePromotionCode({
            userEmail: userEmail,
            promotionCodeId: selectedRow.id,
        });

        if (result) {
            dispatch(reducerActions.emailSent);
            SuccessAlert(t("promoCodeEmailSent"));
        } else {
            dispatch(reducerActions.emailFailed);
        }
    };

    const compareEmailState = (equalTo) => {
        return reducedState.emailState === equalTo;
    };

    const renderActionFeedback = () => {
        switch (reducedState.emailState) {
            case reducerStates.sendClicked:
            case reducerStates.sendingEmail:
                return (
                    <MainLoadingButton
                        buttonText={`${t("undo")} (${counter})`}
                        className="default-orange-outline-btn"
                        iconClassName="fas fa-undo-alt"
                        isDisabled={compareEmailState(
                            reducerStates.sendingEmail
                        )}
                        onClick={handleUndoClicked}
                    />
                );
            case reducerStates.emailSent:
                return (
                    <span className="market-item-type success capitalize">
                        <i className="fas fa-check mr-1"></i>
                        {t("sent")}
                    </span>
                );
            case reducerStates.emailFailed:
                return (
                    <span className="market-item-type text-primary-color-orange capitalize">
                        <i className="fas fa-times mr-1"></i>
                        {t("failed")}
                    </span>
                );
            case reducerStates.initial:
            default:
                return (
                    <BaseActionButton
                        className="table-btn table-btn-blue"
                        icon="fas fa-paper-plane"
                        onClick={handleSendButtonClicked}
                        disabled={!userEmail}
                    />
                );
        }
    };

    return (
        <>
            <BaseAlertModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                includeCancel={false}
                includeSubmit={false}
                iconClassName="fas fa-share-alt"
                title={t("shareTitle", { entity: t("promotionCode") })}
                message={t("shareEmailDescription", {
                    entity: t("promotionCode"),
                })}
            >
                <div className="flex w-full items-center justify-center">
                    <div className="input-group my-4 w-1/2 mr-2">
                        <input
                            type="text"
                            name="email"
                            className="input-element"
                            onChange={handleEmailChange}
                            readOnly={compareEmailState(
                                reducerStates.sendClicked
                            )}
                            value={userEmail}
                        />
                    </div>
                    {renderActionFeedback()}
                </div>
            </BaseAlertModal>
        </>
    );
};

export default ShareModal;
